import { useMutation } from 'react-query';

import { PAYMENTS_BACKEND_URL } from '@/config';
import axios from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';
import { UserProductTypes } from '@/types';

export type ResponseError = {
  status: number;
  detail: string;
};

export type DeleteUserPendingProductProps = {
  mdirectorId: string;
  onErrorCallback: ({ status, detail }: ResponseError, mdirectorId: string) => void;
  onSuccessCallback?: () => void;
};

export const deleteUserPendingProduct = ({
  mdirectorId,
  onErrorCallback,
  onSuccessCallback,
}: DeleteUserPendingProductProps): Promise<UserProductTypes[]> => {
  const endPoint = PAYMENTS_BACKEND_URL + 'my-products/' + mdirectorId;
  return axios
    .delete(endPoint)
    .then((response: any) => {
      onSuccessCallback && onSuccessCallback();
      return response.data;
    })
    .catch((error: any) => {
      const status = error.response.status;
      const detail = error.response.data.detail;
      onErrorCallback && onErrorCallback({ status, detail }, mdirectorId);
      if (error.response.status === 409) {
        return;
      }

      throw Error(`Critical error putting data in ${endPoint}: ${error}`);
    });
};

type UseDeleteUserPendingProductOptions = {
  config?: MutationConfig<typeof deleteUserPendingProduct>;
};

export const useDeleteUserPendingProduct = ({
  config,
}: UseDeleteUserPendingProductOptions = {}) => {
  return useMutation({
    onMutate: async (creatingNotification: any) => {
      await queryClient.cancelQueries(['my-products', creatingNotification]);

      const previousDetails = queryClient.getQueryData<UserProductTypes>([
        'my-products',
        creatingNotification,
      ]);

      queryClient.setQueryData(['my-products', creatingNotification], {
        ...previousDetails,
        ...creatingNotification.data,
      });

      return { previousDetails: previousDetails };
    },
    onError: (_, __, context: any) => {
      if (context?.previousDetails) {
        queryClient.setQueryData(['my-products', context.previousDetails], context.previousDetails);
      }
    },
    onSuccess: (data) => {
      return queryClient.refetchQueries(['my-products', data]);
    },
    ...config,
    mutationFn: deleteUserPendingProduct,
  });
};
